import { Editor as TipTapEditor } from '@tiptap/react'
import { Editor as SlateEditor } from 'slate-react'

export type DictationTargetType =
  | 'input'
  | 'textarea'
  | 'contenteditable'
  | 'tiptap'
  | 'slate'
  | 'unknown'

export interface DictationTarget {
  type: DictationTargetType
  element: HTMLElement | null
  tiptapEditor?: TipTapEditor | null
  slateEditor?: SlateEditor | null
}

export const detectTargetType = (element: HTMLElement | null): DictationTargetType => {
  if (!element) return 'unknown'

  const tagName = element.tagName.toLowerCase()

  if (tagName === 'input') return 'input'
  if (tagName === 'textarea') return 'textarea'

  // Check framework-specific editors BEFORE generic contenteditable, because
  // Slate's root element is a contenteditable div — it would wrongly match the
  // generic check and bypass Slate's state management entirely.
  if (element.closest('.ProseMirror') || element.classList.contains('ProseMirror')) {
    return 'tiptap'
  }
  if (element.closest('[data-slate-editor]') || element.hasAttribute('data-slate-editor')) {
    return 'slate'
  }

  if (element.contentEditable === 'true') return 'contenteditable'

  return 'unknown'
}

/**
 * Best-effort human-readable label for a dictation target (trash bin, analytics).
 *
 * Priority:
 *  1. `data-dictation-label` on the field or any ancestor — explicit, the most
 *     reliable. Add it to a field/editor container to give it a clean name
 *     (e.g. `data-dictation-label="Observations"`), especially for rich editors
 *     (TipTap/Slate) that have no native label.
 *  2. the field's own `aria-label`
 *  3. an associated `<label for=id>`
 *  4. a wrapping `<label>`
 *  5. native `name` / `placeholder`
 *
 * Returns undefined when nothing usable is found.
 */
export const getDictationFieldLabel = (element: HTMLElement | null): string | undefined => {
  if (!element) return undefined

  // 1. Explicit opt-in label, anywhere up the tree.
  const tagged = element.closest('[data-dictation-label]')
  const explicit = tagged?.getAttribute('data-dictation-label')?.trim()
  if (explicit) return explicit

  // 2. The field's own aria-label (kept on the element only — climbing risks
  //    grabbing an unrelated dialog/region label).
  const aria = element.getAttribute('aria-label')?.trim()
  if (aria) return aria

  // 3. Associated <label for=id>.
  if (element.id) {
    const associated = document.querySelector(`label[for="${element.id}"]`)?.textContent?.trim()
    if (associated) return associated
  }

  // 4. Wrapping <label>.
  const wrapping = element.closest('label')?.textContent?.trim()
  if (wrapping) return wrapping

  // 5. Native input hints.
  const name = element.getAttribute('name')?.trim()
  if (name) return name

  const placeholder = element.getAttribute('placeholder')?.trim()
  if (placeholder) return placeholder

  return undefined
}

export const insertTextInNativeInput = (
  element: HTMLInputElement | HTMLTextAreaElement,
  text: string,
): void => {
  const pos = element.selectionStart ?? element.value.length
  const endPos = element.selectionEnd ?? pos
  const newValue = element.value.slice(0, pos) + text + element.value.slice(endPos)

  const nativeInputValueSetter =
    element.tagName === 'TEXTAREA'
      ? Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')?.set
      : Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set

  if (nativeInputValueSetter) {
    nativeInputValueSetter.call(element, newValue)
  } else {
    element.value = newValue
  }

  const newPos = pos + text.length
  element.setSelectionRange(newPos, newPos)

  element.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }))
  element.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }))
}

export const insertTextInContentEditable = (element: HTMLElement, text: string): void => {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0) {
    element.textContent = (element.textContent || '') + text
    return
  }

  const range = selection.getRangeAt(0)

  if (!element.contains(range.commonAncestorContainer)) {
    element.focus()
  }

  range.deleteContents()

  // Gérer les retours à la ligne
  const parts = text.split('\n')
  const fragment = document.createDocumentFragment()

  parts.forEach((part, index) => {
    if (index > 0) {
      fragment.appendChild(document.createElement('br'))
    }
    if (part) {
      fragment.appendChild(document.createTextNode(part))
    }
  })

  range.insertNode(fragment)

  // Positionner le curseur à la fin
  range.collapse(false)
  selection.removeAllRanges()
  selection.addRange(range)
}

export const insertTextInTipTap = (editor: TipTapEditor, text: string): void => {
  requestAnimationFrame(() => {
    if (!editor || editor.isDestroyed) return

    // Convertir les \n en hardBreak pour TipTap
    const parts = text.split('\n')

    if (parts.length === 1) {
      editor.chain().insertContent(text).run()
    } else {
      // Construire le contenu avec des hardBreak
      const content: Array<{ type: string; text?: string }> = []
      parts.forEach((part, index) => {
        if (index > 0) {
          content.push({ type: 'hardBreak' })
        }
        if (part) {
          content.push({ type: 'text', text: part })
        }
      })
      editor.chain().insertContent(content).run()
    }
  })
}

export const insertTextInSlate = (
  editor: SlateEditor | null,
  text: string,
  element: HTMLElement | null,
): void => {
  if (editor) {
    // In slate-react 0.22.x the React component only delegates a fixed set of
    // methods to the slate.Editor controller (applyOperation, command, run,
    // withoutNormalizing, onChange, …). Direct mutation methods like
    // insertText / splitBlock are NOT on the React component — calling them
    // would be undefined.
    // Use ctrl.command(name, ...args) which IS delegated, goes through the full
    // command → applyOperation → onChange cycle and properly updates React state.
    const ctrl = editor as any
    const parts = text.split('\n')
    if (parts.length === 1) {
      ctrl.command('insertText', text)
    } else {
      parts.forEach((part: string, index: number) => {
        if (index > 0) ctrl.command('splitBlock')
        if (part) ctrl.command('insertText', part)
      })
    }
    return
  }

  // Fallback when no editor ref: execCommand on the contenteditable.
  // Use closest() (upward) not querySelector() (downward) since the focused
  // element is usually a child of the [data-slate-editor] contenteditable.
  if (element) {
    const slateElement = element.hasAttribute('data-slate-editor')
      ? element
      : (element.closest('[data-slate-editor]') as HTMLElement | null)

    if (slateElement) {
      slateElement.focus()
      const parts = text.split('\n')
      parts.forEach((part, index) => {
        if (index > 0) document.execCommand('insertParagraph', false)
        if (part) document.execCommand('insertText', false, part)
      })
      return
    }
  }

  console.warn('Slate: ni éditeur ni élément disponible pour insérer le texte')
}

export const removeLastTextFromTarget = (target: DictationTarget, text: string | null): void => {
  switch (target.type) {
    case 'input':
    case 'textarea': {
      if (!text) return
      const el = target.element as HTMLInputElement | HTMLTextAreaElement
      if (!el) return
      const lastIndex = el.value.lastIndexOf(text)
      if (lastIndex === -1) return
      const newValue = el.value.slice(0, lastIndex) + el.value.slice(lastIndex + text.length)
      const setter =
        target.type === 'textarea'
          ? Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')?.set
          : Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set
      if (setter) setter.call(el, newValue)
      else el.value = newValue
      el.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }))
      el.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }))
      break
    }
    case 'tiptap':
    case 'contenteditable': {
      if (!text) break
      const el = target.element
      if (!el) break
      el.focus()
      const sel = window.getSelection()
      if (!sel) break
      for (let i = 0; i < text.length; i++) {
        sel.modify('extend', 'backward', 'character')
      }
      if (!sel.isCollapsed) {
        sel.getRangeAt(0).deleteContents()
        el.dispatchEvent(new Event('input', { bubbles: true }))
      }
      break
    }
    case 'slate':
      // Handled via slateUndoCallbackRef in the provider
      break
  }
}

export const clearTarget = (target: DictationTarget): void => {
  switch (target.type) {
    case 'input':
    case 'textarea': {
      const el = target.element as HTMLInputElement | HTMLTextAreaElement
      if (!el) return
      const setter =
        target.type === 'textarea'
          ? Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')?.set
          : Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set
      if (setter) setter.call(el, '')
      else el.value = ''
      el.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }))
      el.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }))
      break
    }
    case 'tiptap':
    case 'contenteditable': {
      const el = target.element
      if (!el) break
      el.focus()
      document.execCommand('selectAll')
      setTimeout(() => document.execCommand('delete'), 0)
      break
    }
    case 'slate':
      // Handled via slateSetValueCallbackRef in the provider
      break
  }
}

export const insertTextInTarget = (target: DictationTarget, text: string): boolean => {
  try {
    switch (target.type) {
      case 'input':
        // Les inputs ne supportent pas les retours à la ligne, on les remplace par des espaces
        if (target.element) {
          const sanitizedText = text.replace(/\n/g, ' ')
          insertTextInNativeInput(target.element as HTMLInputElement, sanitizedText)
          return true
        }
        break

      case 'textarea':
        if (target.element) {
          insertTextInNativeInput(target.element as HTMLTextAreaElement, text)
          return true
        }
        break

      case 'contenteditable':
        if (target.element) {
          insertTextInContentEditable(target.element, text)
          return true
        }
        break

      case 'tiptap':
        if (target.tiptapEditor) {
          insertTextInTipTap(target.tiptapEditor, text)
          return true
        }
        if (target.element) {
          insertTextInContentEditable(target.element, text)
          return true
        }
        break

      case 'slate':
        insertTextInSlate(target.slateEditor ?? null, text, target.element)
        return true
    }
  } catch (error) {
    console.error('Erreur insertion texte:', error)
    return false
  }

  return false
}
